// @flow
import React, { PureComponent, type ComponentType } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router";
import { getDisplayName } from "data/constants";
import { type MultiSelectionProps } from "containers/MultiSelection";
import { type PaginationHandlerProps } from "containers/PaginationHandler";
import { inPointAction, inPointActionLink } from "components/ItemUriLink";
import * as s from "data/reducers/selectors";
import bowser from "data/bowser";
import { type ConstantsEnum, getDefaultsConfig } from "data/constants";
import { openItemActionPanel } from "components/ItemActionPanel";
import { openMultiSelectionActionPanel } from "components/MultiSelectionActionPanel";
import {
    GridListConstant,
    DocListColumnType,
    type SiteTypeEnum,
    type DocColumnResultDTO,
    type ArrowNavigationSetter,
} from "data/types";
import CommandActionHandler from "components/CommandActions";
import { fetchDocResultFlags } from "data/api";
import { createDraft, finishDraft } from "immer";

type Props = {
    /** current view name */
    viewName: string,
    /** handle click in modal */
    asModal?: boolean,
    /** Redux: current formatId */
    formatId: number,
    /** Redux: current siteType */
    siteType: SiteTypeEnum,
    /** active row index */
    activeRowIndex: number,
    /** whether running under Windows */
    onWindows: boolean,
    /** whether running under Mobile */
    onMobile: boolean,
    /** total number of documents */
    docsTotalCount: number,
    /** document fetch status  */
    docsStatus: ConstantsEnum,
    /** Redux: doclist data rows */
    docsRows: ?Array<any>,
    /** Redux: doclist cols */
    docsCols: ?Array<DocColumnResultDTO>,
    /** Whether inPoint Sync integration is allowed (from appSettings.json/Defaults) */
    inPointSyncIntegration: boolean,
    /** ArrowNavigation Setter Functions */
    postMessage: ?ArrowNavigationSetter,
    /** withMultipleSelection additional props */
    multiSelection: MultiSelectionProps,
    /** Pagination additional props */
    pagination: PaginationHandlerProps,
};

/**
 * handles DocViewer (as modal or not) and provides all Icon/Single/Double event handlers
 */
const DocClickHandler = (WrappedComponent: ComponentType<any>) => {
    class PP extends PureComponent<Props> {
        static displayName = `DocClickHandler(${getDisplayName(
            WrappedComponent
        )})`;

        _showDoc = (
            itemUri: string,
            newWindow: boolean,
            activeRowIndex?: number = null
        ) => {
            const asModal = this.props.asModal !== false;
            return (
                asModal &&
                CommandActionHandler("doc_preview", {
                    itemUri,
                    newWindow,
                    ...this.props.pagination?.getProps?.(activeRowIndex),
                    onReopenWith: /* istanbul ignore next */ (itemUri) =>
                        this._showDoc(itemUri, newWindow, activeRowIndex),
                })
            );
        };

        _getConfig = (
            itemUri: string,
            eventName: string
        ): { skipClick: boolean } => {
            const cfg = getDefaultsConfig(itemUri);
            const skipClick = cfg?.docListClick?.[eventName] === false;
            const newWindow =
                cfg?.docListClick?.[eventName + "NewWindow"] === true;
            return { skipClick, newWindow };
        };

        _onIconClick = (
            e: SyntheticMouseEvent<*>,
            itemUri: string,
            rowIndex: number
        ) => {
            const { skipClick, newWindow } = this._getConfig(
                itemUri,
                "onIconClick"
            );
            const doNewWindow = e.ctrlKey || e.altKey || newWindow;
            e.preventDefault();
            /* istanbul ignore else */
            if (!skipClick) this._showDoc(itemUri, doNewWindow, rowIndex);
            this.props.pagination?.setCurrent?.(rowIndex);
        };

        _onSingleClick = (
            e: SyntheticMouseEvent<*>,
            itemUri: string,
            rowIndex: number
        ) => {
            const { skipClick } = this._getConfig(itemUri, "onSingleClick");
            const prevRowIndex = this.props.activeRowIndex;
            this.props.pagination?.setCurrent?.(rowIndex);
            /* istanbul ignore else */
            if (!skipClick) {
                if (e.ctrlKey) {
                    /* istanbul ignore else */
                    if (!this.props.multiSelection?.multiple) {
                        this.props.multiSelection?.actions.handleToggle(
                            prevRowIndex
                        );
                    }
                    this.props.multiSelection?.actions.handleToggle(rowIndex);
                } else if (e.shiftKey) {
                    this.props.multiSelection?.actions.handleSelectRange(
                        prevRowIndex,
                        rowIndex
                    );
                } else if (this.props.multiSelection?.multiple) {
                    this.props.multiSelection?.disable();
                } else if (this.props.onMobile) {
                    this._showDoc(itemUri, false, rowIndex);
                }
            }
        };

        _onMoreClick = (
            e: SyntheticMouseEvent<*>,
            itemUri: string,
            rowIndex: number,
            columnIndex: number
        ) => {
            const {
                docsCols,
                docsRows,
                formatId,
                tree,
                folder,
                itemUri: itemUriFromProps,
                pagination,
            } = this.props;
            const row = docsRows[rowIndex];
            const extensionColIndex = docsCols.findIndex(
                (col) => col.name === GridListConstant.ExtensionIcon
            );
            const nameColIndex = docsCols.findIndex(
                (col) => col.type === DocListColumnType.WebDavName
            );
            const itemStatusColIndex = docsCols.findIndex(
                (col) => col.name === GridListConstant.IconItemStatus
            );

            this.props.pagination?.setCurrent?.(rowIndex);

            if (
                this.props.multiSelection?.multiple &&
                Object.keys(this.props.multiSelection?.activeRows).length > 1 &&
                this.props.multiSelection?.isSelected(rowIndex)
            ) {
                const folderItemUri =
                    tree?.find((i) => i.id === folder?.currentRow)?.itemUri ??
                    itemUriFromProps;
                // wrong folderItemUri
                const itemUriColIndex = docsCols.findIndex(
                    (col) => col.name === GridListConstant.ItemUri
                );
                openMultiSelectionActionPanel({
                    ...pagination?.getProps?.(rowIndex),
                    activeRows: this.props.multiSelection?.activeRows,
                    mapper: /*istanbul ignore next*/ (row) => ({
                        name: row[nameColIndex],
                        itemUri: row[itemUriColIndex],
                        ext: row[extensionColIndex],
                        formatId,
                        isDoc: true,
                    }),
                    formatId,
                    folderItemUri,
                    rowIndex,
                    columnIndex,
                });
            } else {
                openItemActionPanel({
                    ext:
                        row[extensionColIndex]?.value || row[extensionColIndex],
                    icon: row[extensionColIndex]?.icon,
                    color: row[extensionColIndex]?.color,
                    contextProps: {
                        formatId,
                        itemUri,
                        isDoc: true,
                        name: row[nameColIndex],
                        ext:
                            row[extensionColIndex]?.value ||
                            row[extensionColIndex],
                        itemStatus: row[itemStatusColIndex],
                        siteType: this.props.siteType,
                        rowIndex,
                        columnIndex,
                        ...pagination?.getProps?.(rowIndex),
                    },
                });
            }
        };

        _onDoubleClick = async (
            e: SyntheticMouseEvent<*>,
            itemUri: string,
            rowIndex: number
        ) => {
            const { skipClick } = this._getConfig(itemUri, "onDoubleClick");
            const draft = createDraft({
                newWindow: e.ctrlKey || e.altKey,
                isDocDownloadAllowed: false,
                isDocEditAllowed: false,
                inPointSyncIntegration: false,
            });
            const recipe = async (draft) =>
                Object.assign(
                    draft,
                    await fetchDocResultFlags(itemUri, this.props.formatId)
                );
            await recipe(draft);
            const {
                newWindow,
                isDocDownloadAllowed,
                isDocEditAllowed,
                inPointSyncIntegration,
            } = finishDraft(draft);
            this.props.pagination?.setCurrent?.(rowIndex);
            /* istanbul ignore else */
            if (!skipClick) {
                // editing with inPoint.Sync only allowed if inPointSyncIntegration flag set
                if (
                    isDocEditAllowed &&
                    this.props.onWindows &&
                    inPointSyncIntegration
                ) {
                    window.location.assign(
                        inPointActionLink(inPointAction.edit, itemUri)
                    );
                    return;
                } else if (isDocDownloadAllowed) {
                    const { docsCols, docsRows } = this.props;
                    const row = docsRows[rowIndex];
                    const nameColIndex = docsCols.findIndex(
                        (col) => col.type === DocListColumnType.WebDavName
                    );
                    // TODO #41653 Online/WebDav view/edit for Office documents
                    CommandActionHandler("doc_download", {
                        itemUri,
                        name: row[nameColIndex],
                    });
                    return;
                } else {
                    this._showDoc(itemUri, newWindow, rowIndex);
                }
            }
        };

        render() {
            return (
                <WrappedComponent
                    {...this.props}
                    onIconClick={this._onIconClick}
                    onSingleClick={this._onSingleClick}
                    onDoubleClick={this._onDoubleClick}
                    onMoreClick={this._onMoreClick}
                />
            );
        }
    }

    PP.displayName = `DocClickHandler(${getDisplayName(WrappedComponent)})`;

    const mapStateToProps = (state, ownProps) => ({
        docsRows: s.docsRowsSelector(state),
        docsCols: s.docsColsSelector(state),
        docsStatus: s.docsStatusSelector(state),
        docsTotalCount: s.docsTotalCountSelector(state),
        onMobile: bowser.is("mobile") || bowser.is("tablet"),
        onWindows: bowser.is("windows"),
        formatId: s.breadcrumbFormatIdSelector(state, ownProps.viewName),
        siteType: s.breadcrumbSiteTypeSelector(state, ownProps.viewName),
        ...ownProps,
    });

    return compose(connect(mapStateToProps), withRouter)(PP);
};

export default DocClickHandler;
